import { DocMode } from '@affine/graphql';
import { track } from '@affine/track';
import { InlineCommentManager } from '@blocksuite/affine/inlines/comment';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type {
  BaseSelection,
  DocSnapshot,
  Store,
} from '@blocksuite/affine/store';
import type { BlockStdScope } from '@blocksuite/std';
import {
  effect,
  Entity,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { catchError, filter, first, of, Subject, switchMap, tap } from 'rxjs';
import type { Socket } from 'socket.io-client';

import { createServerSocketConnection } from '../../../utils/socket';
import { WorkspaceServerService } from '../../cloud';
import { type DocDisplayMetaService } from '../../doc-display-meta';
import { GlobalContextService } from '../../global-context';
import { FullscreenRegistry } from '../../streamlain/services/fullscreen-registry';
import type { WorkspaceService } from '../../workspace';
import type { SnapshotHelper } from '../services/snapshot-helper';
import type {
  CommentAttachment,
  CommentId,
  CommentTargetAnchor,
  DocComment,
  DocCommentContent,
  DocCommentReply,
  PendingComment,
} from '../types';
import { DocCommentStore } from './doc-comment-store';
import { findMentions } from './utils';

type DisposeCallback = () => void;

// =============================================================================
// WebSocket Event Payloads (matching backend CommentGateway emissions)
// =============================================================================

/** Backend Comment shape (CommentWithReplies / Comment) */
interface WsComment {
  id: string;
  content: Record<string, unknown>;
  resolved: boolean;
  targetBlockIds: string[];
  createdAt: string; // ISO string over the wire
  updatedAt: string;
  user: { id: string; name: string; avatarUrl: string | null };
  replies?: WsReply[];
}

/** Backend Reply shape */
interface WsReply {
  id: string;
  commentId: string;
  content: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string; avatarUrl: string | null };
}

// Helpers to normalize WebSocket payloads into frontend types
function normalizeWsUser(user: {
  id: string;
  name: string;
  avatarUrl: string | null;
}): DocCommentReply['user'] {
  return { id: user.id, name: user.name, avatarUrl: user.avatarUrl ?? '' };
}

function normalizeWsReply(reply: WsReply): DocCommentReply {
  return {
    id: reply.id,
    commentId: reply.commentId,
    content: reply.content as DocCommentContent,
    createdAt: new Date(reply.createdAt).getTime(),
    updatedAt: new Date(reply.updatedAt).getTime(),
    user: normalizeWsUser(reply.user),
    mentions: reply.content
      ? findMentions((reply.content as DocCommentContent).snapshot?.blocks)
      : [],
  };
}

function normalizeWsComment(comment: WsComment): DocComment {
  return {
    id: comment.id,
    content: comment.content
      ? (comment.content as DocCommentContent)
      : undefined,
    resolved: comment.resolved,
    targetBlockIds: comment.targetBlockIds ?? [],
    targetAnchors: ((comment.content as DocCommentContent | undefined)
      ?.targetAnchors ?? []) as CommentTargetAnchor[],
    createdAt: new Date(comment.createdAt).getTime(),
    updatedAt: new Date(comment.updatedAt).getTime(),
    user: normalizeWsUser(comment.user),
    mentions: comment.content
      ? findMentions((comment.content as DocCommentContent).snapshot?.blocks)
      : [],
    replies: comment.replies?.map(normalizeWsReply) ?? [],
  };
}

type EditingDraft = {
  id: CommentId;
  type: 'comment' | 'reply';
  doc: Store;
  attachments: CommentAttachment[];
};

export class DocCommentEntity extends Entity<{
  docId: string;
  std: BlockStdScope | null;
}> {
  constructor(
    private readonly snapshotHelper: SnapshotHelper,
    private readonly docDisplayMetaService: DocDisplayMetaService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }
  private readonly store = this.framework.createEntity(DocCommentStore, {
    docId: this.props.docId,
    getDocMode: () =>
      this.docMode$.value === 'edgeless' ? DocMode.edgeless : DocMode.page,
    getDocTitle: () => {
      return this.docDisplayMetaService.title$(this.props.docId).value;
    },
  });

  loading$ = new LiveData<boolean>(false);
  comments$ = new LiveData<DocComment[]>([]);

  commentsInEditor$ = new LiveData<string[]>([]);

  // Only one pending comment at a time (for new comments)
  readonly pendingComment$ = new LiveData<PendingComment | null>(null);

  // Only one pending reply at a time
  readonly pendingReply$ = new LiveData<PendingComment | null>(null);

  // Draft state for editing existing comment or reply (only one at a time)
  readonly editingDraft$ = new LiveData<EditingDraft | null>(null);

  private readonly commentAdded$ = new Subject<{
    id: CommentId;
    selections: BaseSelection[];
  }>();
  private readonly commentResolved$ = new Subject<CommentId>();
  private readonly commentDeleted$ = new Subject<CommentId>();
  readonly commentHighlighted$ = new LiveData<CommentId | null>(null);

  private socket: Socket | null = null;

  async addComment(
    selections?: BaseSelection[],
    preview?: string,
    targetBlockIds?: string[],
    targetAnchors?: CommentTargetAnchor[]
  ): Promise<string> {
    // check if there is a pending comment, reuse it
    let pendingComment = this.pendingComment$.value;
    const doc =
      pendingComment?.doc ?? (await this.snapshotHelper.createStore());
    if (!doc) {
      throw new Error('Failed to create doc');
    }
    const id = nanoid();
    pendingComment = {
      id,
      doc,
      preview,
      selections,
      targetBlockIds,
      targetAnchors,
      attachments: [],
    };

    // Replace any existing pending comment (only one at a time)
    this.pendingComment$.setValue(pendingComment);
    return id;
  }

  /**
   * Add a reply to a comment.
   * If reply is provided, the content should @mention the user who is replying to.
   */
  async addReply(commentId: string, reply?: DocCommentReply): Promise<string> {
    // check if there is a pending reply, reuse it
    let pendingReply = this.pendingReply$.value;
    const doc = pendingReply?.doc ?? (await this.snapshotHelper.createStore());
    if (!doc) {
      throw new Error('Failed to create doc');
    }
    const mention: string | undefined = reply?.user.id;
    if (mention) {
      // insert mention at the end of the paragraph
      const paragraph = doc.getModelsByFlavour('affine:paragraph').at(-1);
      if (paragraph) {
        paragraph.text?.insert(' ', paragraph.text.length, {
          mention: {
            member: mention,
          },
        });
      }
    }
    const id = nanoid();
    pendingReply = {
      id,
      doc,
      commentId,
      attachments: [],
    };
    // Replace any existing pending reply (only one at a time)
    this.pendingReply$.setValue(pendingReply);
    return id;
  }

  dismissDraftComment(): void {
    this.pendingComment$.setValue(null);
  }

  dismissDraftReply(): void {
    this.pendingReply$.setValue(null);
  }

  /**
   * Start editing an existing comment or reply.
   * This will dismiss any previous editing draft so that only one can exist.
   */
  async startEdit(
    id: CommentId,
    type: 'comment' | 'reply',
    snapshot: DocSnapshot,
    attachments: CommentAttachment[]
  ): Promise<void> {
    const doc = await this.snapshotHelper.createStore(snapshot);
    if (!doc) {
      throw new Error('Failed to create doc for editing');
    }
    this.editingDraft$.setValue({ id, type, doc, attachments });
  }

  /** Commit current editing draft (if any) */
  async commitEditing(): Promise<void> {
    const draft = this.editingDraft$.value;
    if (!draft) return;

    const snapshot = this.snapshotHelper.getSnapshot(draft.doc);
    if (!snapshot) {
      throw new Error('Failed to get snapshot');
    }

    if (draft.type === 'comment') {
      await this.updateComment(draft.id, {
        snapshot,
        attachments: draft.attachments,
      });
    } else {
      await this.updateReply(draft.id, {
        snapshot,
        attachments: draft.attachments,
      });
    }
    track.$.commentPanel.$.editComment({
      type: draft.type === 'comment' ? 'root' : 'node',
    });
    this.editingDraft$.setValue(null);
    this.revalidate();
  }

  /** Dismiss current editing draft without saving */
  dismissDraftEditing(): void {
    this.editingDraft$.setValue(null);
  }

  get docMode$() {
    return this.framework.get(GlobalContextService).globalContext.docMode.$;
  }

  async commitComment(id: string): Promise<void> {
    const pendingComment = this.pendingComment$.value;
    if (!pendingComment || pendingComment.id !== id) {
      console.warn('Pending comment not found:', id);
      return;
    }
    const { doc, preview, attachments, targetBlockIds, targetAnchors } =
      pendingComment;
    const snapshot = this.snapshotHelper.getSnapshot(doc);
    if (!snapshot) {
      throw new Error('Failed to get snapshot');
    }
    const mentions = findMentions(snapshot.blocks);
    const comment = await this.store.createComment({
      content: {
        snapshot,
        preview,
        mode: this.docMode$.value ?? 'page',
        attachments,
        targetAnchors,
      },
      mentions,
      targetBlockIds,
    });
    const currentComments = this.comments$.value;
    this.comments$.setValue([...currentComments, comment]);
    this.commentAdded$.next({
      id: comment.id,
      selections: pendingComment.selections || [],
    });
    // for block's preview, it will be something like <Paragraph>
    // extract the block type from the preview
    const blockType = preview?.match(/<([^>]+)>/)?.[1];
    track.$.commentPanel.$.createComment({
      type: 'root',
      withAttachment: (attachments?.length ?? 0) > 0,
      withMention: mentions.length > 0,
      category: blockType
        ? blockType
        : (this.docMode$.value ?? 'page') === 'page'
          ? 'Page'
          : 'Note',
    });
    this.pendingComment$.setValue(null);
    this.revalidate();
  }

  async createTargetComment(input: {
    preview: string;
    targetBlockIds: string[];
    targetAnchors: CommentTargetAnchor[];
  }): Promise<string> {
    const doc = await this.snapshotHelper.createStore();
    if (!doc) {
      throw new Error('Failed to create doc');
    }
    const snapshot = this.snapshotHelper.getSnapshot(doc);
    if (!snapshot) {
      throw new Error('Failed to get snapshot');
    }
    const comment = await this.store.createComment({
      content: {
        snapshot,
        preview: input.preview,
        mode: this.docMode$.value ?? 'page',
        attachments: [],
        targetAnchors: input.targetAnchors,
      },
      mentions: [],
      targetBlockIds: input.targetBlockIds,
    });
    this.comments$.setValue([...this.comments$.value, comment]);
    this.revalidate();
    return comment.id;
  }

  async commitReply(id: string): Promise<void> {
    const pendingReply = this.pendingReply$.value;
    if (!pendingReply || pendingReply.id !== id) {
      console.warn('Pending reply not found:', id);
      return;
    }
    const { doc, attachments } = pendingReply;
    const snapshot = this.snapshotHelper.getSnapshot(doc);
    if (!snapshot) {
      throw new Error('Failed to get snapshot');
    }

    if (!pendingReply.commentId) {
      throw new Error('Pending reply has no commentId');
    }

    const mentions = findMentions(snapshot.blocks);
    const reply = await this.store.createReply(pendingReply.commentId, {
      content: {
        snapshot,
        attachments,
      },
      mentions,
    });
    const currentComments = this.comments$.value;
    const updatedComments = currentComments.map(comment =>
      comment.id === pendingReply.commentId
        ? { ...comment, replies: [...(comment.replies || []), reply] }
        : comment
    );
    this.comments$.setValue(updatedComments);
    track.$.commentPanel.$.createComment({
      type: 'node',
      withAttachment: (attachments?.length ?? 0) > 0,
      withMention: mentions.length > 0,
      category: (this.docMode$.value ?? 'page') === 'page' ? 'Page' : 'Note',
    });
    this.pendingReply$.setValue(null);
    this.revalidate();
  }

  async deleteComment(id: string): Promise<void> {
    await this.store.deleteComment(id);
    const currentComments = this.comments$.value;
    this.comments$.setValue(currentComments.filter(c => c.id !== id));
    track.$.commentPanel.$.deleteComment({ type: 'root' });
    this.commentDeleted$.next(id);
    this.revalidate();
  }

  async deleteReply(replyId: string): Promise<void> {
    await this.store.deleteReply(replyId);
    const currentComments = this.comments$.value;
    const updatedComments = currentComments.map(comment => {
      return {
        ...comment,
        replies: comment.replies?.filter(r => r.id !== replyId),
      };
    });
    this.comments$.setValue(updatedComments);
    track.$.commentPanel.$.deleteComment({ type: 'node' });
    this.revalidate();
  }

  /**
   * Upload an attachment file for the draft/editing comment/reply.
   * @param file File to upload
   * @returns
   */
  uploadCommentAttachment = async (
    id: string,
    file: File,
    pending: PendingComment | EditingDraft
  ): Promise<string> => {
    // check if the given pending comment is the same as the current comment or reply
    const isPendingComment = pending.id === this.pendingComment$.value?.id;
    const isPendingReply = pending.id === this.pendingReply$.value?.id;
    const isEditingDraft = pending.id === this.editingDraft$.value?.id;
    if (!isPendingComment && !isPendingReply && !isEditingDraft) {
      throw new Error('Pending comment/reply not found');
    }
    const url = await this.store.uploadCommentAttachment(file);

    // todo: should be immutable
    pending.attachments.push({
      id,
      url,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    });

    if (isPendingComment) {
      this.pendingComment$.setValue(pending as PendingComment);
    } else if (isPendingReply) {
      this.pendingReply$.setValue(pending as PendingComment);
    } else if (isEditingDraft) {
      this.editingDraft$.setValue(pending as EditingDraft);
    }
    return url;
  };

  async updateComment(id: string, content: DocCommentContent): Promise<void> {
    await this.store.updateComment(id, { content });
    const currentComments = this.comments$.value;
    const updatedComments = currentComments.map(comment =>
      comment.id === id ? { ...comment, content } : comment
    );
    this.comments$.setValue(updatedComments);
    this.revalidate();
  }

  async updateReply(id: string, content: DocCommentContent): Promise<void> {
    await this.store.updateReply(id, { content });
    const currentComments = this.comments$.value;
    const updatedComments = currentComments.map(comment =>
      comment.id === id ? { ...comment, content } : comment
    );
    this.comments$.setValue(updatedComments);
    this.revalidate();
  }

  updatePendingComment(id: string, patch: Partial<PendingComment>): void {
    const pendingComment = this.pendingComment$.value;
    if (!pendingComment || pendingComment.id !== id) {
      throw new Error('Pending comment not found');
    }
    this.pendingComment$.setValue({ ...pendingComment, ...patch });
  }

  updatePendingReply(id: string, patch: Partial<PendingComment>): void {
    const pendingReply = this.pendingReply$.value;
    if (!pendingReply || pendingReply.id !== id) {
      throw new Error('Pending reply not found');
    }
    this.pendingReply$.setValue({ ...pendingReply, ...patch });
  }

  updateEditingDraft(id: string, patch: Partial<EditingDraft>): void {
    const draft = this.editingDraft$.value;
    if (!draft || draft.id !== id) {
      throw new Error('Editing draft not found');
    }
    this.editingDraft$.setValue({ ...draft, ...patch });
  }

  async resolveComment(id: CommentId, resolved: boolean): Promise<void> {
    try {
      await this.store.resolveComment(id, resolved);

      // Update local state
      const currentComments = this.comments$.value;
      const updatedComments = currentComments.map(comment =>
        comment.id === id ? { ...comment, resolved } : comment
      );
      this.comments$.setValue(updatedComments);

      this.commentResolved$.next(id);
      track.$.commentPanel.$.resolveComment({
        type: resolved ? 'on' : 'off',
      });
      this.revalidate();
    } catch (error) {
      console.error('Failed to resolve comment:', error);
      throw error;
    }
  }

  highlightComment(id: CommentId | null): void {
    this.commentHighlighted$.next(id);
  }

  /**
   * Navigate to the block(s) referenced by a comment.
   *
   * **Page mode**: scrolls the block element into view and applies a temporary
   * highlight animation (CSS class `comment-highlight-flash`).
   *
   * **Edgeless mode**: pans the GFX viewport to center on the element and
   * applies the same temporary highlight.
   *
   * Sets `commentHighlighted$` for the duration of the highlight (2 s).
   */
  navigateToComment(commentId: string): void {
    const comment = this.comments$.value.find(c => c.id === commentId);
    if (!comment) {
      console.warn('[DocComment] Comment not found for navigation:', commentId);
      return;
    }

    const targetBlockIds = comment.targetBlockIds;
    const pdfAnchor = comment.targetAnchors.find(
      anchor => anchor.type === 'pdf-text'
    );
    if (pdfAnchor) {
      this.framework
        .get(FullscreenRegistry)
        .open('attachment', pdfAnchor.blockId);
      this.commentHighlighted$.next(commentId);
      window.dispatchEvent(
        new CustomEvent('streamlain:pdf-comment-focus', {
          detail: {
            commentId,
            blockId: pdfAnchor.blockId,
            pageIndex: pdfAnchor.pageIndex,
          },
        })
      );
      return;
    }

    if (!targetBlockIds || targetBlockIds.length === 0) {
      // Legacy comment without block reference – nothing to navigate to.
      return;
    }

    const std = this.props.std;
    if (!std) {
      // Editor not mounted yet – skip silently.
      return;
    }

    const mode = this.docMode$.value ?? 'page';
    const firstBlockId = targetBlockIds[0];

    // Set highlight state (cleared after 2 s)
    this.commentHighlighted$.next(commentId);
    const highlightTimer = setTimeout(() => {
      // Only clear if still highlighting the same comment
      if (this.commentHighlighted$.value === commentId) {
        this.commentHighlighted$.next(null);
      }
    }, 2000);

    if (mode === 'edgeless') {
      this._navigateEdgeless(std, firstBlockId, highlightTimer);
    } else {
      this._navigatePage(std, firstBlockId, highlightTimer);
    }
  }

  // ---------------------------------------------------------------------------
  // Page mode: scroll block into view + temporary highlight flash
  // ---------------------------------------------------------------------------
  private _navigatePage(
    std: BlockStdScope,
    blockId: string,
    _highlightTimer: ReturnType<typeof setTimeout>
  ): void {
    const blockElement = std.view.getBlock(blockId);
    if (!blockElement) {
      console.warn(
        '[DocComment] Block element not found in DOM for id:',
        blockId
      );
      void _highlightTimer; // keep timer running for sidebar highlight
      return;
    }

    // Scroll into viewport
    blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Apply temporary CSS highlight flash
    blockElement.classList.add('comment-highlight-flash');
    setTimeout(() => {
      blockElement.classList.remove('comment-highlight-flash');
    }, 2000);
  }

  // ---------------------------------------------------------------------------
  // Edgeless mode: pan viewport to element + temporary highlight flash
  // ---------------------------------------------------------------------------
  private _navigateEdgeless(
    std: BlockStdScope,
    elementId: string,
    _highlightTimer: ReturnType<typeof setTimeout>
  ): void {
    const gfx = std.get(GfxControllerIdentifier);
    if (!gfx) {
      console.warn('[DocComment] GfxController not available');
      void _highlightTimer;
      return;
    }

    const element = gfx.getElementById(elementId);
    if (!element) {
      console.warn('[DocComment] GFX element not found for id:', elementId);
      void _highlightTimer;
      return;
    }

    // Pan viewport to center on the element with some padding
    if ('elementBound' in element) {
      gfx.viewport.setViewportByBound(
        element.elementBound,
        [80, 80, 80, 80],
        true
      );
    }

    // Try to apply visual highlight to the DOM element as well
    const blockElement = std.view.getBlock(elementId);
    if (blockElement) {
      blockElement.classList.add('comment-highlight-flash');
      setTimeout(() => {
        blockElement.classList.remove('comment-highlight-flash');
      }, 2000);
    }
  }

  async getComments(
    type: 'resolved' | 'unresolved' | 'all' = 'all'
  ): Promise<CommentId[]> {
    return new Promise<CommentId[]>(resolve => {
      this.revalidate();
      this.loading$
        .pipe(
          filter(loading => !loading),
          first()
        )
        .subscribe(() => {
          resolve(
            this.comments$.value
              .filter(comment =>
                type === 'all'
                  ? true
                  : type === 'resolved'
                    ? comment.resolved
                    : !comment.resolved
              )
              .map(comment => comment.id)
          );
        });
    });
  }

  onCommentAdded(
    callback: (id: CommentId, selections: BaseSelection[]) => void
  ): DisposeCallback {
    const subscription = this.commentAdded$.subscribe(({ id, selections }) =>
      callback(id, selections)
    );
    return () => subscription.unsubscribe();
  }

  onCommentResolved(callback: (id: CommentId) => void): DisposeCallback {
    const subscription = this.commentResolved$.subscribe(callback);
    return () => subscription.unsubscribe();
  }

  onCommentDeleted(callback: (id: CommentId) => void): DisposeCallback {
    const subscription = this.commentDeleted$.subscribe(callback);
    return () => subscription.unsubscribe();
  }

  onCommentHighlighted(
    callback: (id: CommentId | null) => void
  ): DisposeCallback {
    const subscription = this.commentHighlighted$.subscribe(callback);
    return () => subscription.unsubscribe();
  }

  /**
   * Start real-time comment updates.
   * 1. Initial load via GraphQL (full comment list)
   * 2. Connect to /comment WebSocket namespace for incremental updates
   */
  start(): void {
    // Tear down any previous connection
    this.stop();

    // Initial load via GraphQL
    this.revalidate();
    this.revalidateCommentsInEditor();

    // Connect to WebSocket for real-time updates
    this.connectWebSocket();
  }

  stop(): void {
    this.disconnectWebSocket();
  }

  // ===========================================================================
  // WebSocket Connection
  // ===========================================================================

  private connectWebSocket(): void {
    const serverService = this.framework.get(WorkspaceServerService);
    const server = serverService.server;
    if (!server) {
      console.warn(
        '[DocComment] No server available, skipping WebSocket connection'
      );
      return;
    }

    this.socket = createServerSocketConnection(server, '/comment');

    this.socket.on('connect', () => {
      // Subscribe to the doc room
      this.socket?.emit('comment:subscribe', {
        workspaceId: this.workspaceService.workspace.id,
        docId: this.props.docId,
      });
    });

    this.socket.on('connect_error', error => {
      console.error('[DocComment] WebSocket connection error:', error);
    });

    // Listen for comment events
    this.socket.on('comment:created', this.handleWsCommentCreated);
    this.socket.on('comment:updated', this.handleWsCommentUpdated);
    this.socket.on('comment:deleted', this.handleWsCommentDeleted);
    this.socket.on('comment:resolved', this.handleWsCommentResolved);
    this.socket.on('reply:created', this.handleWsReplyCreated);
    this.socket.on('reply:updated', this.handleWsReplyUpdated);
    this.socket.on('reply:deleted', this.handleWsReplyDeleted);
  }

  private disconnectWebSocket(): void {
    if (!this.socket) return;

    try {
      // Unsubscribe from the doc room
      this.socket.emit('comment:unsubscribe', {
        workspaceId: this.workspaceService.workspace.id,
        docId: this.props.docId,
      });

      // Remove event listeners
      this.socket.off('comment:created', this.handleWsCommentCreated);
      this.socket.off('comment:updated', this.handleWsCommentUpdated);
      this.socket.off('comment:deleted', this.handleWsCommentDeleted);
      this.socket.off('comment:resolved', this.handleWsCommentResolved);
      this.socket.off('reply:created', this.handleWsReplyCreated);
      this.socket.off('reply:updated', this.handleWsReplyUpdated);
      this.socket.off('reply:deleted', this.handleWsReplyDeleted);

      this.socket.disconnect();
    } catch (error) {
      console.warn('[DocComment] Error during WebSocket disconnect:', error);
    }

    this.socket = null;
  }

  // ===========================================================================
  // WebSocket Event Handlers
  // ===========================================================================

  private readonly handleWsCommentCreated = (data: WsComment): void => {
    const comment = normalizeWsComment(data);
    const current = this.comments$.value;
    // Avoid duplicates (e.g. if the current user created it locally)
    if (!current.some(c => c.id === comment.id)) {
      this.comments$.setValue([...current, comment]);
      this.revalidateCommentsInEditor();
    }
  };

  private readonly handleWsCommentUpdated = (data: WsComment): void => {
    const updated = normalizeWsComment(data);
    const current = this.comments$.value;
    const index = current.findIndex(c => c.id === updated.id);
    if (index !== -1) {
      const newComments = [...current];
      // Preserve existing replies — the update event may not include them
      newComments[index] = { ...updated, replies: current[index].replies };
      this.comments$.setValue(newComments);
    }
  };

  private readonly handleWsCommentDeleted = (data: { id: string }): void => {
    const current = this.comments$.value;
    const filtered = current.filter(c => c.id !== data.id);
    if (filtered.length !== current.length) {
      this.comments$.setValue(filtered);
      this.revalidateCommentsInEditor();
    }
  };

  private readonly handleWsCommentResolved = (data: {
    id: string;
    resolved: boolean;
  }): void => {
    const current = this.comments$.value;
    const index = current.findIndex(c => c.id === data.id);
    if (index !== -1) {
      const newComments = [...current];
      newComments[index] = { ...current[index], resolved: data.resolved };
      this.comments$.setValue(newComments);
    }
  };

  private readonly handleWsReplyCreated = (data: WsReply): void => {
    const reply = normalizeWsReply(data);
    const current = this.comments$.value;
    const parentIndex = current.findIndex(c => c.id === reply.commentId);
    if (parentIndex === -1) return;

    const parent = current[parentIndex];
    const replies = parent.replies ?? [];
    // Avoid duplicates
    if (replies.some(r => r.id === reply.id)) return;

    const newComments = [...current];
    newComments[parentIndex] = { ...parent, replies: [...replies, reply] };
    this.comments$.setValue(newComments);
  };

  private readonly handleWsReplyUpdated = (data: WsReply): void => {
    const reply = normalizeWsReply(data);
    const current = this.comments$.value;
    const parentIndex = current.findIndex(c => c.id === reply.commentId);
    if (parentIndex === -1) return;

    const parent = current[parentIndex];
    const replies = [...(parent.replies ?? [])];
    const replyIndex = replies.findIndex(r => r.id === reply.id);
    if (replyIndex === -1) return;

    replies[replyIndex] = reply;
    const newComments = [...current];
    newComments[parentIndex] = { ...parent, replies };
    this.comments$.setValue(newComments);
  };

  private readonly handleWsReplyDeleted = (data: {
    id: string;
    commentId: string;
  }): void => {
    const current = this.comments$.value;
    const parentIndex = current.findIndex(c => c.id === data.commentId);
    if (parentIndex === -1) return;

    const parent = current[parentIndex];
    const filtered = (parent.replies ?? []).filter(r => r.id !== data.id);
    if (filtered.length === (parent.replies ?? []).length) return;

    const newComments = [...current];
    newComments[parentIndex] = { ...parent, replies: filtered };
    this.comments$.setValue(newComments);
  };

  revalidate = effect(
    switchMap(() => {
      return fromPromise(async () => {
        const allComments: DocComment[] = [];
        let cursor = '';
        this.revalidateCommentsInEditor();
        // Fetch all pages of comments
        while (true) {
          const result = await this.store.listComments({ after: cursor });
          allComments.push(...result.comments);
          cursor = result.endCursor;
          if (!result.hasNextPage) {
            break;
          }
        }

        return allComments;
      }).pipe(
        tap(allComments => {
          this.revalidateCommentsInEditor();
          // Update state with all comments
          this.comments$.setValue(allComments);
        }),
        onStart(() => this.loading$.setValue(true)),
        onComplete(() => this.loading$.setValue(false)),
        catchError(error => {
          console.error('Failed to fetch comments:', error);
          this.loading$.setValue(false);
          return of([]);
        })
      );
    })
  );

  private readonly revalidateCommentsInEditor = () => {
    this.commentsInEditor$.setValue(this.getCommentsInEditor());
  };

  private getCommentsInEditor(): string[] {
    const inlineCommentManager = this.props.std?.get(InlineCommentManager);
    if (!inlineCommentManager) {
      return [];
    }
    return inlineCommentManager.getCommentsInEditor();
  }

  override dispose(): void {
    this.stop();
    this.commentAdded$.complete();
    this.commentResolved$.complete();
    this.commentDeleted$.complete();
    this.commentHighlighted$.complete();
    super.dispose();
  }
}
